import { Node } from "@tiptap/core";
import type { SuggestionOptions } from "@tiptap/suggestion";
import Suggestion from "@tiptap/suggestion";

import { getSuggestionOptions } from "./utils/get-default-suggestion-attributes";

import { TemplateFormat } from "@/components/Playground/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface VariableMentionOptions<SuggestionItem = any> {
  mode: TemplateFormat;
  suggestions: Omit<SuggestionOptions<SuggestionItem>, "editor">[];
  suggestion: Omit<SuggestionOptions<SuggestionItem>, "editor"> | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface VariableMentionStorage<SuggestionItem = any> {
  suggestions: SuggestionOptions<SuggestionItem>[];
  getSuggestionFromChar: (char: string) => SuggestionOptions<SuggestionItem> | null;
}

export const VariableMention = Node.create<VariableMentionOptions, VariableMentionStorage>({
  name: "variableMention",

  priority: 101,

  addStorage() {
    return {
      suggestions: [],
      getSuggestionFromChar: () => null,
    };
  },

  addOptions() {
    return {
      mode: "f-string" as TemplateFormat,
      suggestions: [],
      suggestion: null,
    };
  },

  group: "inline",

  inline: true,

  selectable: false,

  atom: true,

  addProseMirrorPlugins() {
    return this.storage.suggestions.map(Suggestion);
  },

  onBeforeCreate() {
    const { mode } = this.options;

    // Prefer explicit suggestions array; else, use single suggestion or default
    const baseSuggestions = this.options.suggestions.length
      ? this.options.suggestions
      : [
          this.options.suggestion ?? {
            char: "{",
            command: ({ editor, range, props }) => {
              const content = props.label || props.id;
              const wrappedContent = mode === "jinja2" ? `{{ ${content} }}` : `{${content}}`;
              editor.chain().focus().deleteRange(range).insertContent(wrappedContent).run();
            },
          },
        ];

    // Map suggestions, always close over the correct mode
    this.storage.suggestions = baseSuggestions.map(suggestion =>
      getSuggestionOptions({
        editor: this.editor,
        overrideSuggestionOptions: {
          ...suggestion,
          command: ({ editor, range, props }) => {
            // Always use up-to-date mode from options
            const content = props.label || props.id;
            const wrappedContent = mode === "jinja2" ? `{{ ${content} }}` : `{${content}}`;
            editor.chain().focus().deleteRange(range).insertContent(wrappedContent).run();
          },
        },
        extensionName: this.name,
        char: suggestion.char || "{",
      }),
    );

    this.storage.getSuggestionFromChar = char => {
      const suggestion = this.storage.suggestions.find(s => s.char === char);
      if (suggestion) {
        return suggestion;
      }
      if (this.storage.suggestions.length) {
        return this.storage.suggestions[0];
      }
      return null;
    };
  },
});
